import { useModoConsultaEvaluacion } from '../../../../hooks/useModoConsultaEvaluacion';
import React, { useState, useContext } from 'react';
import { AppContext } from '../../../../Context';
import { collection, addDoc } from "firebase/firestore";
import { db } from '../../../../firebase/firebase';
import { Form, Button, Container, Card, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';

const M14_CuadroSinoptico = ({ cerrarModal }) => {
    const { currentUser } = useContext(AppContext);
    const modoConsulta = useModoConsultaEvaluacion();
    const [step, setStep] = useState(1);
    
    const [respuestas, setRespuestas] = useState({
        identificacion: {
            tema_principal: '',
            periodos: '',
            caracteristicas_clave: ''
        },
        cuadro: {
            micenico: '',
            homerico: '',
            arcaico: '',
            clasico: '',
            helenistico: ''
        }
    });

    const textoBase = `LITERATURA GRIEGA

La literatura griega se divide en varios periodos históricos:

- Periodo micénico: primeros registros culturales y mitológicos.
- Periodo homérico: destacan obras como la Ilíada y la Odisea.
- Periodo arcaico: desarrollo de la poesía lírica.
- Periodo clásico: auge del teatro, la filosofía y la democracia.
- Periodo helenístico: expansión cultural tras las conquistas de Alejandro Magno.

Cada periodo presenta características propias en cuanto a estilo, temas y desarrollo cultural.`;

    const handleIdentificacion = (campo, valor) => {
        setRespuestas(prev => ({
            ...prev,
            identificacion: { ...prev.identificacion, [campo]: valor }
        }));
    };

    const handleCuadro = (campo, valor) => {
        setRespuestas(prev => ({
            ...prev,
            cuadro: { ...prev.cuadro, [campo]: valor }
        }));
    };

    const nextStep = () => {
        if (!modoConsulta && (step === 2)) {
            const { tema_principal, periodos, caracteristicas_clave } = respuestas.identificacion;
            if (!tema_principal.trim() || !periodos.trim() || !caracteristicas_clave.trim()) {
                Swal.fire({ icon: 'warning', title: 'Incompleto', text: 'Por favor completa todas las áreas de identificación.' });
                return;
            }
        }
        setStep(prev => prev + 1);
    };

    const prevStep = () => setStep(prev => prev - 1);

    const calcularPuntaje = () => {
        let puntaje = 0;
        
        // FASE 1: Identificación (8 pts)
        if (respuestas.identificacion.tema_principal.toLowerCase().includes('literatura')) puntaje += 2;
        if (respuestas.identificacion.periodos.length > 15) puntaje += 3;
        if (respuestas.identificacion.caracteristicas_clave.length > 15) puntaje += 3;
        
        // FASE 2: Cuadro Sinóptico (12 pts)
        const completadas = Object.values(respuestas.cuadro).filter(val => val.trim().length > 5).length;
        puntaje += Math.floor((completadas / 5) * 12);

        return puntaje;
    };

    const enviarResultados = async () => {
        if (modoConsulta) { cerrarModal?.(); return; }

        const completadas = Object.values(respuestas.cuadro).filter(val => val.trim() !== '');
        if (completadas.length < 5) {
            Swal.fire({ icon: 'warning', title: 'Incompleto', text: 'Debes completar las características de todos los periodos históricos.' });
            return;
        }

        const puntajeFinal = calcularPuntaje();

        try {
            await addDoc(collection(db, "evaluaciones"), {
                userId: currentUser.uid,
                nombre: "Elaboración de Cuadros Sinópticos (Literatura Griega)",
                nivel: "Avanzado",
                modulo: "14",
                createdAt: new Date(),
                tipo: "Cuadro Sinóptico Avanzado",
                respuestas: respuestas,
                puntaje: puntajeFinal,
                puntajeMaximo: 20
            });
            Swal.fire({
                title: '¡Evaluación Enviada!',
                text: `Resultados enviados con éxito. Puntaje estimado: ${puntajeFinal}/20.`,
                icon: 'success',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Excelente'
            }).then((result) => {
                if (result.isConfirmed) {
                    cerrarModal();
                }
            });
        } catch (error) {
            console.error("Error al enviar las respuestas: ", error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Error al enviar las respuestas.' });
        }
    };

    const periodosUI = [
        { id: 'micenico', nombre: 'Micénico', color: 'primary' },
        { id: 'homerico', nombre: 'Homérico', color: 'success' },
        { id: 'arcaico', nombre: 'Arcaico', color: 'info' },
        { id: 'clasico', nombre: 'Clásico', color: 'warning' },
        { id: 'helenistico', nombre: 'Helenístico', color: 'danger' }
    ];

    return (
        <Container>
            <h3 className="text-center mb-4">Construcción Avanzada de Cuadro Sinóptico</h3>
            
            {step === 1 && (
                <div>
                    <Card className="mb-4 shadow-sm border-left-primary">
                        <Card.Body>
                            <Card.Title>FASE 1: Análisis (Lectura)</Card.Title>
                            <Card.Text>Lee el siguiente texto histórico y analiza su estructura temporal.</Card.Text>
                            <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify' }}>
                                {textoBase}
                            </div>
                        </Card.Body>
                    </Card>
                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="primary" size="lg" onClick={nextStep}>Siguiente: Identificación</Button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div>
                    <Card className="mb-4 shadow-sm border-left-warning">
                        <Card.Body>
                            <Card.Title>FASE 1: Identificación</Card.Title>
                            <Card.Text>Extrae la información clave para estructurarla lógicamente.</Card.Text>
                        </Card.Body>
                    </Card>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Tema Principal:</Form.Label>
                            <Form.Control type="text" placeholder="Ej: Literatura Griega" value={respuestas.identificacion.tema_principal} onChange={e => handleIdentificacion('tema_principal', e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Periodos Históricos:</Form.Label>
                            <Form.Control as="textarea" rows={3} placeholder="Lista los periodos mencionados..." value={respuestas.identificacion.periodos} onChange={e => handleIdentificacion('periodos', e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Características Clave Generales:</Form.Label>
                            <Form.Control as="textarea" rows={3} placeholder="¿Qué elementos definen estos periodos?" value={respuestas.identificacion.caracteristicas_clave} onChange={e => handleIdentificacion('caracteristicas_clave', e.target.value)} />
                        </Form.Group>
                    </Form>
                    <div className="d-flex justify-content-between mt-4">
                        <Button variant="secondary" onClick={prevStep}>Anterior</Button>
                        <Button variant="primary" size="lg" onClick={nextStep}>Siguiente: Cuadro Sinóptico</Button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div>
                    <Card className="mb-4 shadow-sm border-left-success">
                        <Card.Body>
                            <Card.Title>FASES 2 y 3: Representación Visual</Card.Title>
                            <Card.Text>Construye el cuadro sinóptico distribuyendo las características en la línea de tiempo (distribución horizontal en desktop).</Card.Text>
                        </Card.Body>
                    </Card>
                    
                    <div className="text-center mb-5">
                        <h4 className="p-3 bg-dark text-white rounded d-inline-block shadow-lg">LITERATURA GRIEGA</h4>
                        <div className="d-none d-md-block" style={{ height: '30px', borderLeft: '3px solid #343a40', width: '3px', margin: '0 auto' }}></div>
                        <div className="d-none d-md-block" style={{ height: '3px', backgroundColor: '#343a40', width: '80%', margin: '0 auto' }}></div>
                    </div>

                    <Form>
                        <Row className="g-3 justify-content-center flex-column flex-md-row">
                            {periodosUI.map((periodo, index) => (
                                <Col xs={12} md={true} key={index}>
                                    <Card className={`h-100 shadow-sm border-${periodo.color} text-center`}>
                                        <Card.Header className={`bg-${periodo.color} text-white fw-bold`}>
                                            {periodo.nombre}
                                        </Card.Header>
                                        <Card.Body className="bg-light p-2">
                                            <Form.Control 
                                                as="textarea" 
                                                rows={4} 
                                                placeholder="Características del periodo..." 
                                                value={respuestas.cuadro[periodo.id]}
                                                onChange={e => handleCuadro(periodo.id, e.target.value)}
                                                style={{ resize: 'none', fontSize: '0.9rem' }}
                                            />
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Form>
                    <div className="d-flex justify-content-between mt-5">
                        <Button variant="secondary" onClick={prevStep}>Anterior</Button>
                        <Button variant="success" size="lg" onClick={enviarResultados}>Finalizar y Enviar</Button>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default M14_CuadroSinoptico;